"use client";

import * as Popover from "@radix-ui/react-popover";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import { SWITCHER_SEARCH_INPUT_CLASS } from "@/components/ui/switcher-popover-motion";
import { DIALOG_ABOVE_OVERLAY_Z_CLASS } from "@/fetch-ui/_logic/constants";
import { FetchSwitcherRow } from "@/fetch-ui/FetchSwitcherRow";
import { FetchSwitcherSearchField } from "@/fetch-ui/FetchSwitcherSearchField";
import { useDismissableLayer } from "@/hooks/useDismissableLayer";

import { useCloseOnScroll } from "../../_hooks/useCloseOnScroll";
import { POPOVER_MOTION, SURFACE_FEEL } from "../../_logic/motion";

/** One choice in a {@link PopoverSelect}. */
export interface PopoverSelectOption {
  value: string;
  label: string;
  /** Muted second line, e.g. a currency name or event dates. */
  sublabel?: string;
  /** Extra words the search matches on. */
  keywords?: string;
}

interface PopoverSelectProps {
  ariaLabel: string;
  /** The choices, or a function of the search text that returns them already filtered and ranked. */
  options: PopoverSelectOption[] | ((query: string) => PopoverSelectOption[]);
  value: string | null;
  onChange: (value: string) => void;
  /** Shows a search field; for long lists (events, currencies). */
  searchPlaceholder?: string;
  /** Shown in place of "No matches" when the list is empty, e.g. a loading note or a load error with a retry. */
  emptyState?: ReactNode;
  /**
   * The trigger's text when nothing is selected, whether it stretches to its container, and its
   * look: `compact` is a small inline chip for a row, `accent` colors it indigo (a value that
   * differs from the default), `label` replaces the selected option's label.
   */
  trigger?: { placeholder?: string; fullWidth?: boolean; compact?: boolean; accent?: boolean; label?: string };
}

const TRIGGER_CLASS =
  "inline-flex h-9 items-center justify-between gap-2 rounded-full border border-gray-200 bg-white px-3.5 text-sm font-medium text-gray-800 transition-colors hover:border-gray-300 hover:bg-gray-50";

const COMPACT_TRIGGER_CLASS =
  "-mx-1 inline-flex h-6 items-center gap-0.5 rounded-md px-1 text-xs font-medium transition-colors hover:bg-gray-100";

/**
 * Pill trigger with a searchable, keyboard-navigable popover list, built from the switcher
 * popover pieces (`FetchSwitcherSearchField`, `FetchSwitcherRow`) so it reads like the org and
 * event switchers. Arrow keys move, Enter picks, Escape closes (inside a modal, only the list).
 * @param props - See {@link PopoverSelectProps}.
 * @returns The select.
 * @throws Never.
 */
export const PopoverSelect = ({
  ariaLabel,
  options,
  value,
  onChange,
  searchPlaceholder,
  emptyState,
  trigger = {},
}: PopoverSelectProps) => {
  const { placeholder = "Select", fullWidth = false, compact = false, accent = false, label } = trigger;

  // Hooks
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const reduceMotion = useReducedMotion();

  // States
  const [open, setOpen] = useState(false);
  const contentRef = useCloseOnScroll<HTMLDivElement>(open, () => setOpenState(false));
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  // Derived
  const filtered = useMemo(() => {
    if (typeof options === "function") return options(query.trim());
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) =>
      `${option.label} ${option.sublabel ?? ""} ${option.keywords ?? ""}`.toLowerCase().includes(term)
    );
  }, [options, query]);
  const resting = typeof options === "function" ? options("") : options;
  const selected = resting.find((option) => option.value === value) ?? null;
  const triggerText = label ?? selected?.label ?? placeholder;
  const activeIndex = Math.min(active, Math.max(filtered.length - 1, 0));
  const optionId = (index: number) => `${listboxId}-${index}`;

  // Handlers
  const setOpenState = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
    if (next)
      setActive(
        Math.max(
          0,
          resting.findIndex((option) => option.value === value)
        )
      );
  };

  const pick = (next: string) => {
    onChange(next);
    setOpenState(false);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(Math.min(activeIndex + 1, filtered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(Math.max(activeIndex - 1, 0));
    } else if (event.key === "Enter") {
      // Only claim Enter when it picks a row, so a button in the empty state still activates.
      const option = filtered[activeIndex];
      if (!option) return;
      event.preventDefault();
      pick(option.value);
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      requestAnimationFrame(() => document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: "nearest" }));
    }
  };

  // Joins the modal stack so Escape inside a modal closes only this list. Outside presses close
  // through Radix's own dismiss layer.
  useDismissableLayer({ isActive: open, onDismiss: (reason) => reason === "escape" && setOpenState(false) });

  // Render
  return (
    <Popover.Root open={open} onOpenChange={setOpenState}>
      <Popover.Trigger asChild>
        {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
        <button
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          className={`${compact ? COMPACT_TRIGGER_CLASS : TRIGGER_CLASS} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${fullWidth ? "w-full" : ""} ${open ? (compact ? "bg-gray-100" : "border-gray-300 bg-gray-50") : ""} ${accent ? "text-indigo-600" : ""}`}
        >
          <span title={triggerText} className={`truncate ${selected || label ? "" : "text-gray-500"}`}>
            {triggerText}
          </span>
          <ChevronDown
            className={`${compact ? "size-3" : "size-3.5"} shrink-0 ${accent ? "" : "text-gray-500"} transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className={DIALOG_ABOVE_OVERLAY_Z_CLASS}
          onOpenAutoFocus={(event) => {
            if (!searchPlaceholder) return;
            event.preventDefault();
            inputRef.current?.focus();
          }}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <motion.div
            ref={contentRef}
            initial={reduceMotion ? false : POPOVER_MOTION.initial}
            animate={POPOVER_MOTION.animate}
            transition={POPOVER_MOTION.transition}
            onKeyDown={handleKeyDown}
            style={{ transformOrigin: "var(--radix-popover-content-transform-origin)" }}
            className={`w-(--radix-popover-trigger-width) min-w-[240px] max-w-[min(360px,calc(100vw-24px))] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl outline-none ${SURFACE_FEEL}`}
          >
            {searchPlaceholder && (
              <FetchSwitcherSearchField
                inputRef={inputRef}
                value={query}
                onChange={(next) => {
                  setQuery(next);
                  setActive(0);
                }}
                placeholder={searchPlaceholder}
                inputClassName={SWITCHER_SEARCH_INPUT_CLASS}
                comboboxProps={{
                  "aria-controls": listboxId,
                  "aria-activedescendant": filtered.length ? optionId(activeIndex) : undefined,
                }}
              />
            )}
            <div
              role="listbox"
              id={listboxId}
              aria-label={ariaLabel}
              tabIndex={searchPlaceholder ? undefined : 0}
              aria-activedescendant={searchPlaceholder || !filtered.length ? undefined : optionId(activeIndex)}
              className="max-h-[280px] overflow-y-auto p-1 focus:outline-none"
            >
              {filtered.length === 0
                ? (emptyState ?? <p className="px-3 py-6 text-center text-sm font-medium text-gray-500">No matches</p>)
                : filtered.map((option, index) => (
                    <FetchSwitcherRow
                      key={option.value}
                      id={optionId(index)}
                      role="option"
                      icon={null}
                      label={option.label}
                      sublabel={option.sublabel}
                      isSelected={option.value === value}
                      isHighlighted={index === activeIndex}
                      onClick={() => pick(option.value)}
                    />
                  ))}
            </div>
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
